// Inline stroke icons for the chat cards (24px grid, currentColor), drawn to
// match the approved design. Decorative: every one is aria-hidden.

import { ReactNode } from "react";

interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const Stroke = ({
  size = 16,
  strokeWidth = 2,
  className,
  children,
}: IconProps & { children: ReactNode }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const CheckIcon = (props: IconProps) => (
  <Stroke strokeWidth={2.6} {...props}>
    <path d="M20 6 9 17l-5-5" />
  </Stroke>
);

export const DashIcon = (props: IconProps) => (
  <Stroke strokeWidth={2.6} {...props}>
    <path d="M6 12h12" />
  </Stroke>
);

export const ArrowUpRightIcon = (props: IconProps) => (
  <Stroke strokeWidth={2.2} className="cw-icon-flip" {...props}>
    <path d="M7 17 17 7" />
    <path d="M8 7h9v9" />
  </Stroke>
);

export const DownloadIcon = (props: IconProps) => (
  <Stroke {...props}>
    <path d="M12 4v11" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 20h14" />
  </Stroke>
);

export const CardIcon = (props: IconProps) => (
  <Stroke {...props}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
    <path d="M15 15h3" />
  </Stroke>
);

export const AlertIcon = (props: IconProps) => (
  <Stroke {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <path d="M12 16h.01" />
  </Stroke>
);

export const InfoIcon = (props: IconProps) => (
  <Stroke {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </Stroke>
);

export const PlusIcon = (props: IconProps) => (
  <Stroke strokeWidth={2.4} {...props}>
    <path d="M12 5v14M5 12h14" />
  </Stroke>
);

export const CopyIcon = (props: IconProps) => (
  <Stroke {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </Stroke>
);

export const BoxIcon = (props: IconProps) => (
  <Stroke {...props}>
    <path d="M21 8 12 3 3 8v8l9 5 9-5z" />
    <path d="m3 8 9 5 9-5" />
    <path d="M12 13v8" />
  </Stroke>
);

export const StarIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z" />
  </svg>
);

export const CursorIcon = ({
  size = 15,
  className,
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m4 3 7 18 2.6-7.4L21 11z" />
  </svg>
);

export const ChevronUpIcon = (props: IconProps) => (
  <Stroke strokeWidth={2.4} {...props}>
    <path d="m6 15 6-6 6 6" />
  </Stroke>
);

export const LightbulbIcon = (props: IconProps) => (
  <Stroke {...props}>
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M12 3a6 6 0 0 0-3.6 10.8c.6.5 1 1.2 1.1 2V16h5v-.2c.1-.8.5-1.5 1.1-2A6 6 0 0 0 12 3z" />
  </Stroke>
);
